import {
  useRef,
  useEffect,
  useState,
  useCallback,
  lazy,
  Suspense,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Flex,
  Icon,
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Spinner,
} from '@chakra-ui/react';
import { useShowToast, type IToastFunction } from '@/hooks/useShowToast';
import { KText } from '@/ui-library/components/text';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import { selectTimeToJump } from '@/store/interview-video/selectors';
import { updateCurrentTime } from '@/store/interview-video';
import {
  setIsPlaying,
  setIsMute,
  setPlaybackSpeed,
  setProgress,
  setDuration,
} from '@/store/interview-video';
import {
  selectIsMute,
  selectPlaybackSpeed,
  selectCurrentTime,
} from '@/store/interview-video/selectors';
import { InterviewTranscriptPanel } from '@/components/shared/interviews-revamp/interview-transcript/InterviewTranscriptPanel';
import { InterviewSummary } from '@/components/shared/interviews-revamp/component/InterviewSummary';
import { type InterviewType } from '@/types/interview';
import { Minimize01 } from '@kula-ai/icons/outline/Minimize01';
import { useHotkeys } from 'react-hotkeys-hook';

// Split out so only users who open a recording pay for the v10 media stack.
const RecordingPlayer = lazy(() =>
  import('@/components/shared/interviews-revamp/interview-transcript/RecordingPlayer').then(
    (m) => ({ default: m.RecordingPlayer })
  )
);

const DOWNLOAD_FILENAME = 'interview-recording.mp4';
const COLLAPSE_LABEL = 'Collapse player';
const DOWNLOAD_FAILED_TITLE = 'Failed to download. Please try again.';
const DOWNLOAD_FAILED_TOAST_ID = 'interview-recording-download-failed';

interface ModalVideoPlayerProps {
  videoUrl: string;
  transcriptData: any;
  currentTime: number;
  handleTimeJump: (_time: number, _shouldPlay?: boolean) => void;
  interview: InterviewType;
  isLoading: boolean;
  onClose: () => void;
  /** Restricted to job managers and recruiters on the job. */
  canDownload?: boolean;
}

export const ModalVideoPlayer = ({
  videoUrl,
  transcriptData,
  currentTime,
  handleTimeJump,
  interview,
  isLoading: isTranscriptLoading,
  onClose = () => {},
  canDownload = false,
}: ModalVideoPlayerProps) => {
  const dispatch = useDispatch();
  const showToast: IToastFunction = useShowToast();
  const isMute = useSelector(selectIsMute);
  const playbackSpeed = useSelector(selectPlaybackSpeed);
  const timeJumpValue = useSelector(selectTimeToJump);
  const reduxCurrentTime = useSelector(selectCurrentTime);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<string>('recording');
  const transcriptPanelRef = useRef<any>(null);
  const playerContainerRef = useRef<HTMLDivElement>(null);

  const tabOptions = [
    {
      label: 'Summary',
      value: 'summary',
    },
    {
      label: 'Recording',
      value: 'recording',
    },
  ];

  const videoRef = useRef<HTMLVideoElement | null>(null);
  // State as well as a ref: the player is lazy, so the element appears after
  // mount and only a re-render can attach the listeners below.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const attachVideo = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    setVideoEl(node);
  }, []);

  // In a ref so the mount effect has no reactive deps — re-running it would
  // fight the user's own controls.
  const restoreStateRef = useRef({
    currentTime: reduxCurrentTime,
    isMute,
    playbackSpeed,
  });

  // Redux stays the source of truth shared with the mini player and transcript.
  // videoEl is only the trigger; the ref is the mutable handle.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const restore = restoreStateRef.current;
    video.currentTime = restore.currentTime || 0;
    video.muted = restore.isMute;
    video.playbackRate = restore.playbackSpeed;

    const onLoadedMetadata = () => dispatch(setDuration(video.duration || 0));
    const onTimeUpdate = () => {
      const time = video.currentTime || 0;
      dispatch(setProgress(time));
      dispatch(updateCurrentTime(time));
    };
    const onPlay = () => dispatch(setIsPlaying(true));
    const onPause = () => dispatch(setIsPlaying(false));
    const onEnded = () => dispatch(setIsPlaying(false));
    const onVolumeChange = () => dispatch(setIsMute(video.muted));
    const onRateChange = () =>
      dispatch(setPlaybackSpeed(video.playbackRate || 1));
    const onCanPlay = () => setIsReady(true);

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    video.addEventListener('volumechange', onVolumeChange);
    video.addEventListener('ratechange', onRateChange);
    video.addEventListener('canplay', onCanPlay);

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('volumechange', onVolumeChange);
      video.removeEventListener('ratechange', onRateChange);
      video.removeEventListener('canplay', onCanPlay);
    };
  }, [videoEl, dispatch]);

  useEffect(() => {
    const video = videoRef.current;
    if (timeJumpValue && video) {
      video.currentTime = timeJumpValue / 1000;
    }
  }, [timeJumpValue]);

  // Any interaction in the player hands scroll back to transcript auto-follow.
  useEffect(() => {
    const container = playerContainerRef.current;
    if (!container) return;

    const handleInteraction = () => {
      transcriptPanelRef.current?.resetManualScroll?.();
    };

    container.addEventListener('click', handleInteraction, true);
    container.addEventListener('touchstart', handleInteraction, true);
    container.addEventListener('pointerdown', handleInteraction, true);

    return () => {
      container.removeEventListener('click', handleInteraction, true);
      container.removeEventListener('touchstart', handleInteraction, true);
      container.removeEventListener('pointerdown', handleInteraction, true);
    };
  }, []);

  const skipBy = useCallback((seconds: number): void => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = (video.currentTime || 0) + seconds;
    }
  }, []);

  // useShowToast is a fresh fn each render; a ref keeps handleDownload stable.
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const handleDownload = useCallback((): void => {
    void downloadFileFromUrl({
      url: videoUrl,
      filename: DOWNLOAD_FILENAME,
      // Without this the helper only console.errors; the id stops stacking.
      onError: () => {
        showToastRef.current({
          id: DOWNLOAD_FAILED_TOAST_ID,
          title: DOWNLOAD_FAILED_TITLE,
          status: 'error',
        });
      },
    });
  }, [videoUrl]);

  useHotkeys('left', () => skipBy(-5), { enabled: isReady });
  useHotkeys('right', () => skipBy(5), { enabled: isReady });

  return (
    <Flex
      height="100%"
      maxHeight="100%"
      width="100%"
      bg="#000"
      borderRadius="lg"
      overflow="hidden"
    >
      {/* Video Section */}
      <Flex
        flex="2"
        alignItems="center"
        justifyContent="center"
        bg="#000"
        height="100%"
        minHeight="0"
      >
        <Box
          ref={playerContainerRef}
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          position="relative"
        >
          <Suspense fallback={<Spinner size="sm" color="primary-500" />}>
            <RecordingPlayer
              src={videoUrl}
              canDownload={canDownload}
              onDownload={handleDownload}
              videoRef={attachVideo}
            />
          </Suspense>
          {/* Overlaid because video.js owns the control bar now. */}
          <Flex
            position="absolute"
            top="16px"
            left="16px"
            zIndex={2}
            bg="rgba(0,0,0,0.6)"
            borderRadius="md"
            p="8px"
          >
            <Icon
              as={Minimize01}
              w="20px"
              h="20px"
              color="base-white"
              cursor="pointer"
              onClick={onClose}
              role="button"
              aria-label={COLLAPSE_LABEL}
            />
          </Flex>
        </Box>
      </Flex>
      {/* Transcript/Recording Panel */}
      <Flex
        flex="1.2"
        flexDir="column"
        bg="#fff"
        height="100%"
        minHeight="0"
        borderTopRightRadius="16px"
        borderBottomRightRadius="16px"
        boxShadow="0 0 24px rgba(0,0,0,0.08)"
        overflowY="auto"
      >
        <Tabs
          display="flex"
          flexDir="column"
          flex="1"
          h="100%"
          alignItems="stretch"
          index={tabOptions.findIndex((option) => option?.value === currentTab)}
          isLazy={true}
        >
          <TabList>
            {tabOptions.map((option) => {
              return (
                <Tab
                  key={option?.value}
                  _active={{ boxShadow: '' }}
                  _focus={{ boxShadow: '' }}
                  _selected={{
                    color: 'ats-primary',
                    borderColor: 'ats-primary',
                  }}
                  onClick={() => {
                    setCurrentTab(option?.value);
                  }}
                  alignItems="center"
                  mx="lg"
                  pt="4px"
                  pb="12px"
                  px="0px"
                  minH="38px"
                  mt="18px"
                >
                  <KText color="inherit" size="sm-medium">
                    {option?.label}
                  </KText>
                </Tab>
              );
            })}
          </TabList>
          <TabPanels h="100%" overflowY="hidden">
            <TabPanel p="0" h="100%" overflowY="hidden" flex="1">
              <InterviewSummary interview={interview} />
            </TabPanel>
            <TabPanel p="0" h="100%" overflowY="hidden" flex="1">
              <InterviewTranscriptPanel
                ref={transcriptPanelRef}
                data={transcriptData}
                currentTime={currentTime}
                handleTimeJump={handleTimeJump}
                playerType="modal"
                isLoading={isTranscriptLoading}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
    </Flex>
  );
};
